import { EdgelessLegacySlotIdentifier } from '@blocksuite/affine-block-surface';
import type { RootBlockModel } from '@blocksuite/affine-model';
import { IS_MOBILE } from '@blocksuite/global/env';
import { WidgetComponent, WidgetViewExtension } from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';
import { effect } from '@preact/signals-core';
import { css, html, nothing } from 'lit';
import { state } from 'lit/decorators.js';
import { literal, unsafeStatic } from 'lit/static-html.js';

export const AFFINE_EDGELESS_ZOOM_TOOLBAR_WIDGET =
  'affine-edgeless-zoom-toolbar-widget';

export class AffineEdgelessZoomToolbarWidget extends WidgetComponent<RootBlockModel> {
  static override styles = css`
    :host {
      position: absolute;
      bottom: var(--affine-edgeless-zoom-toolbar-bottom, 20px);
      left: 12px;
      z-index: var(--affine-z-index-popover);
      display: flex;
      justify-content: center;
      pointer-events: none;
      -webkit-user-select: none;
      user-select: none;
    }

    mobile-zoom-ruler {
      pointer-events: auto;
      /* A PILL DO ZOOM SAI DE BAIXO DA BARRA DE FERRAMENTAS (card 1864613608097317974).

         A toolbar do edgeless reserva uma folga lateral de 64px de cada lado
         (SAFE_AREA_WIDTH, em edgeless-toolbar.ts) e nunca invade essa faixa. Esse numero foi
         dimensionado para o zoom do DESKTOP: o zoom-bar-toggle-button mede 36px, e com o
         left: 12px do host ele termina em 48 — cabe com folga.

         O mobile-zoom-ruler mede 107px. Ele termina em 119, e 119 nao cabe em 64 — em largura
         de tela NENHUMA. Medido no dev: num iPhone Pro Max (430px) a toolbar comeca em 83 e a
         pill vai ate 119, com a pill POR BAIXO; num iPad (810px) a toolbar cresce para 662px,
         comeca em 74, e a sobreposicao continua. O operador leu isso como o 62% cortado; ele
         nao estava cortado, estava coberto.

         ⚠️ Por isso a ancora e o COMPONENTE, e nao a largura. A primeira versao deste patch
         usava um @container de 520px e resolvia o celular deixando o iPad exatamente como
         estava — porque a toolbar CRESCE com a tela, e o comeco dela nao acompanha. Foi medir
         o segundo aparelho que mostrou isso. Onde o ruler existe ha colisao; onde ele nao
         existe (desktop) nada muda, porque esta regra nem casa.

         O deslocamento e margem, nao bottom: o host ja e posicionado por
         --affine-edgeless-zoom-toolbar-bottom, que o bundle mobile define com a safe area.
         Somar aqui preserva aquele valor em vez de escrever por cima dele.

         68px = a altura da toolbar (64) mais uma folga de 4. */
      margin-bottom: 68px;
    }

    @container viewport (width <= 1200px) {
      edgeless-zoom-toolbar {
        display: none;
      }
    }

    @container viewport (width > 1200px) {
      zoom-bar-toggle-button {
        display: none;
      }
    }
  `;

  get edgeless() {
    return this.block;
  }

  get gfx() {
    return this.std.get(GfxControllerIdentifier);
  }

  override connectedCallback() {
    super.connectedCallback();

    this.disposables.add(
      effect(() => {
        const currentTool = this.gfx.tool.currentToolName$.value;

        if (currentTool !== 'frameNavigator') {
          this._hide = false;
        }
        this.requestUpdate();
      })
    );
  }

  override firstUpdated() {
    const { disposables, std } = this;
    const slots = std.get(EdgelessLegacySlotIdentifier);

    disposables.add(
      slots.navigatorSettingUpdated.subscribe(({ hideToolbar }) => {
        if (hideToolbar !== undefined) {
          this._hide = hideToolbar;
        }
      })
    );
  }

  override render() {
    if (this._hide) {
      return nothing;
    }

    if (IS_MOBILE) {
      return html`<mobile-zoom-ruler .std=${this.std}></mobile-zoom-ruler>`;
    }

    return html`
      <edgeless-zoom-toolbar .std=${this.std}></edgeless-zoom-toolbar>
      <zoom-bar-toggle-button .std=${this.std}></zoom-bar-toggle-button>
    `;
  }

  @state()
  private accessor _hide = false;
}

export const edgelessZoomToolbarWidget = WidgetViewExtension(
  'affine:page',
  AFFINE_EDGELESS_ZOOM_TOOLBAR_WIDGET,
  literal`${unsafeStatic(AFFINE_EDGELESS_ZOOM_TOOLBAR_WIDGET)}`
);
